// ============================================================
// LayerRenderer.jsx — memoized, hooks always at top level
// ============================================================
import useEntity from 'entities/hooks/use-entity';
import React, { useMemo } from 'react';
import { Box, Flex } from 'rebass';
import NewTextLayer from './NewTextLayer';
import NewImageLayer from './NewImageLayer';

const debug = false;

function randomBorder() {
    if (debug) {
        const color = '#' + Math.floor(Math.random() * 16777215).toString(16);
        const width = Math.floor(Math.random() * 10) + 1;
        const styles = ['solid', 'dashed', 'dotted', 'double', 'groove'];
        const style = styles[Math.floor(Math.random() * styles.length)];
        return `${width}px ${style} ${color}`;
    }
    return undefined;
}

// ✅ Memoized — only re-renders when id, type, zoom, or children change
const LayerRenderer = React.memo(function LayerRenderer({
    id,
    zoom,
    layerProps = undefined,
    children,
    type,
    positionOverride,
    ...props
}) {
    // ✅ Both hooks unconditionally at top level — hook count never varies
    const { entity: frontTemplateLayer } = useEntity({ id, type });

    // frontTemplateLayer comes from the Redux entity store (via useEntity),
    // completely independent of any `layer` object passed down the render
    // tree — so a client-side move/resize (usePageLayerEditor) has no way to
    // reach this component's actual rendered position/size except through
    // this explicit override, applied only while the layer is genuinely
    // being edited (see getOverrideIfDirty).
    const effectiveTemplateLayer = positionOverride
        ? {
            ...frontTemplateLayer,
            ...positionOverride,
            'relative-xpos': positionOverride.xpos,
            'relative-ypos': positionOverride.ypos,
        }
        : frontTemplateLayer;

    const parentLayerId = frontTemplateLayer?.['media-block-id'];

    // ✅ Always called — empty object when no parent, avoids conditional hook
    const { entity: parentLayer } = useEntity(
        parentLayerId ? { id: parentLayerId, type: 'book-page-layers' } : {}
    );

    // ✅ Memoized photo resolution — avoids recalculation on re-renders
    const resolvedPhoto = useMemo(() => {
        if (!frontTemplateLayer) return null;
        const canvas = frontTemplateLayer?.['canvas-image'];
        if (canvas && Object.keys(canvas).length > 0) return canvas;
        return (
            frontTemplateLayer?.['photo'] ||
            frontTemplateLayer?.['source-media'] ||
            null
        );
    }, [frontTemplateLayer]);

    // ✅ Early returns AFTER all hooks — this is correct per Rules of Hooks
    if (children) return children;
    if (parentLayerId && !parentLayer) return null;

    // ─── Case 1: layer has a parent container (media-block) ───────────────
    if (parentLayer?.type === 'Books::PageLayer') {
        const alignmentStyles = {
            width: parentLayer?.width,
            height: parentLayer?.height,
            position: 'absolute',
        };

        return (
            <Flex id={'media-block ' + parentLayer.id} sx={alignmentStyles}>
                {frontTemplateLayer?.type === 'Books::PageTextLayer' && (
                    <NewTextLayer
                        randomBorder={randomBorder()}
                        layerId={id}
                        {...props}
                        {...frontTemplateLayer}
                        renderedAsChild
                        zoom={zoom}
                        fontAlignment={frontTemplateLayer?.['font-alignment']}
                        fontFamily={frontTemplateLayer?.['font-name']}
                        fontWeight={frontTemplateLayer?.['font-weight']}
                        fontSize={frontTemplateLayer?.['font-size']}
                        color={frontTemplateLayer?.['font-colour']}
                        text={frontTemplateLayer?.['text']}
                        xpos={frontTemplateLayer?.['relative-xpos']}
                        ypos={frontTemplateLayer?.['relative-ypos']}
                    />
                )}
                {frontTemplateLayer?.type === 'Books::PageImageLayer' && (
                    <NewImageLayer
                        randomBorder={randomBorder()}
                        layerId={id}
                        {...props}
                        {...frontTemplateLayer}
                        renderedAsChild
                        photo={
                            Object.keys(
                                frontTemplateLayer?.['canvas-image'] || {}
                            ).length > 0
                                ? frontTemplateLayer?.['canvas-image']
                                : frontTemplateLayer?.['photo']
                        }
                        zoom={zoom}
                        xpos={frontTemplateLayer?.['relative-xpos']}
                        ypos={frontTemplateLayer?.['relative-ypos']}
                    />
                )}
            </Flex>
        );
    }

    // ─── Case 2: Text layer ────────────────────────────────────────────────
    if (
        frontTemplateLayer?.type === 'Books::PageTextLayer' ||
        frontTemplateLayer?.type === 'Books::PagePossibilityTextLayer'
    ) {
        return (
            <Box id={frontTemplateLayer?.id}>
                <NewTextLayer
                    randomBorder={randomBorder()}
                    layerId={id}
                    {...props}
                    {...effectiveTemplateLayer}
                    zoom={zoom}
                    xpos={effectiveTemplateLayer?.xpos}
                    ypos={effectiveTemplateLayer?.ypos}
                    fontAlignment={frontTemplateLayer?.['font-alignment']}
                    fontFamily={frontTemplateLayer?.['font-name']}
                    fontWeight={frontTemplateLayer?.['font-weight']}
                    fontSize={frontTemplateLayer?.['font-size']}
                    color={frontTemplateLayer?.['font-colour']}
                    text={frontTemplateLayer?.['text']}
                />
            </Box>
        );
    }

    // ─── Case 3: Image layer ───────────────────────────────────────────────
    if (
        frontTemplateLayer?.type === 'Books::PageImageLayer' ||
        frontTemplateLayer?.type === 'Books::PagePossibilityImageLayer'
    ) {
        return (
            <Box id={frontTemplateLayer?.id + ` relative-xpos:${effectiveTemplateLayer?.['relative-xpos']}px  relative-ypos:${effectiveTemplateLayer?.['relative-ypos']}px`} sx={props.isFromPossibilityReview && {
                position: 'absolute',
                left: `${effectiveTemplateLayer?.['relative-xpos']}px`,
                top: `${effectiveTemplateLayer?.['relative-ypos']}px`,
            }}>
                <NewImageLayer
                    randomBorder={randomBorder()}
                    layerId={id}
                    {...props}
                    {...effectiveTemplateLayer}
                    photo={resolvedPhoto}
                    zoom={zoom}
                />
            </Box>
        );
    }

    return null;
});

export default LayerRenderer;
